import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAdmin } from "@/contexts/AdminContext";
import { toast } from "sonner";
import { Save, ArrowLeft, Upload, ChevronDown, ChevronUp, Search } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

export default function AdminPageEdit() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { api } = useAdmin();
  const [sections, setSections] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [uploading, setUploading] = useState(false);

  const load = () => api.get(`/pages/${slug}`).then(r => setSections(r.data)).catch(() => {});
  useEffect(() => { load(); }, [slug]);

  const toggle = (id) => setExpanded(s => ({ ...s, [id]: !s[id] }));

  const updateField = (sectionId, field, value) => {
    setSections(ss => ss.map(s => s.id === sectionId ? { ...s, [field]: value } : s));
  };

  const updateItemField = (sectionId, itemIdx, field, value) => {
    setSections(ss => ss.map(s => {
      if (s.id !== sectionId) return s;
      const items = [...(s.items || [])];
      items[itemIdx] = { ...items[itemIdx], [field]: value };
      return { ...s, items };
    }));
  };

  const uploadImage = async (e, sectionId) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateField(sectionId, "image_url", `${API}/files/${res.data.path}`);
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const saveSection = async (section) => {
    try {
      const { id, ...data } = section;
      await api.put(`/admin/pages/${section.id}`, data);
      toast.success("Section saved");
    } catch { toast.error("Failed to save"); }
  };

  const saveAll = async () => {
    try {
      await Promise.all(sections.map(s => { const { id, ...data } = s; return api.put(`/admin/pages/${s.id}`, data); }));
      toast.success("All sections saved");
    } catch { toast.error("Failed to save some sections"); }
  };

  const heroSection = sections.find(s => s.section_key === "hero");

  return (
    <div data-testid="admin-page-edit">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/admin/pages")} className="text-slate-500 hover:text-[#0A1628]"><ArrowLeft size={16} /></button>
          <h1 className="font-heading text-2xl text-[#0A1628] font-medium capitalize">{slug} Page</h1>
        </div>
        <button onClick={saveAll} data-testid="save-all-sections" className="flex items-center gap-2 bg-[#0A1628] text-white text-xs px-5 py-2.5 uppercase tracking-wider hover:bg-[#1a2c4e] rounded-[2px]">
          <Save size={14} /> Save All
        </button>
      </div>

      {/* SEO Settings Panel */}
      {heroSection && (
        <div className="bg-white border border-slate-200 rounded-[2px] p-5 mb-4">
          <div className="flex items-center gap-2 mb-4">
            <Search size={14} className="text-[#C5A059]" />
            <h3 className="text-xs uppercase tracking-wider text-slate-400">Page SEO Settings</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Meta Title</label>
              <input
                type="text"
                data-testid="page-meta-title"
                value={heroSection.meta_title || ""}
                onChange={e => updateField(heroSection.id, "meta_title", e.target.value)}
                placeholder={`${slug.charAt(0).toUpperCase() + slug.slice(1)} | Equaliser Legal LLP`}
                className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Meta Description</label>
              <input
                type="text"
                data-testid="page-meta-description"
                value={heroSection.meta_description || ""}
                onChange={e => updateField(heroSection.id, "meta_description", e.target.value)}
                placeholder="Brief description for search engines (155 chars)"
                className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]"
              />
            </div>
          </div>
          <p className="text-[10px] text-slate-300 mt-2">These values appear in search engine results. Leave blank to use defaults.</p>
        </div>
      )}

      <div className="space-y-3">
        {sections.map(section => (
          <div key={section.id} className="bg-white border border-slate-200 rounded-[2px] overflow-hidden">
            <button onClick={() => toggle(section.id)} className="w-full flex items-center justify-between p-4 hover:bg-slate-50 text-left">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-[#C5A059] block">{section.section_key}</span>
                <span className="text-sm text-[#0A1628] font-medium">{section.title || "(untitled)"}</span>
              </div>
              {expanded[section.id] ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
            </button>

            {expanded[section.id] && (
              <div className="p-4 pt-0 border-t border-slate-100 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="text-xs text-slate-400 block mb-1">Title</label><input type="text" value={section.title || ""} onChange={e => updateField(section.id, "title", e.target.value)} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" /></div>
                  <div><label className="text-xs text-slate-400 block mb-1">Subtitle / Label</label><input type="text" value={section.subtitle || ""} onChange={e => updateField(section.id, "subtitle", e.target.value)} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" /></div>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Body (Rich Text)</label>
                  <div className="quill-admin">
                    <ReactQuill
                      theme="snow"
                      value={section.body || ""}
                      onChange={val => updateField(section.id, "body", val)}
                      modules={quillModules}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="text-xs text-slate-400 block mb-1">CTA Text</label><input type="text" value={section.cta_text || ""} onChange={e => updateField(section.id, "cta_text", e.target.value)} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" /></div>
                  <div><label className="text-xs text-slate-400 block mb-1">CTA Link</label><input type="text" value={section.cta_link || ""} onChange={e => updateField(section.id, "cta_link", e.target.value)} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" /></div>
                </div>
                {section.cta2_text !== undefined && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="text-xs text-slate-400 block mb-1">CTA 2 Text</label><input type="text" value={section.cta2_text || ""} onChange={e => updateField(section.id, "cta2_text", e.target.value)} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" /></div>
                    <div><label className="text-xs text-slate-400 block mb-1">CTA 2 Link</label><input type="text" value={section.cta2_link || ""} onChange={e => updateField(section.id, "cta2_link", e.target.value)} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" /></div>
                  </div>
                )}
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Image</label>
                  <div className="flex items-center gap-3">
                    {section.image_url && <img src={section.image_url} alt="" className="w-20 h-14 object-cover rounded-[2px] border" />}
                    <label className="flex items-center gap-2 text-xs border border-slate-200 px-3 py-2 rounded-[2px] cursor-pointer hover:border-[#0A1628]">
                      <Upload size={12} /> {uploading ? "..." : "Upload"}
                      <input type="file" accept="image/*" onChange={e => uploadImage(e, section.id)} className="hidden" />
                    </label>
                    <input type="text" value={section.image_url || ""} onChange={e => updateField(section.id, "image_url", e.target.value)} placeholder="Or paste URL" className="flex-1 border border-slate-200 px-3 py-2 text-sm rounded-[2px]" />
                  </div>
                </div>

                {/* Items editor */}
                {section.items && section.items.length > 0 && (
                  <div>
                    <label className="text-xs text-slate-400 block mb-2">Items ({section.items.length})</label>
                    <div className="space-y-3">
                      {section.items.map((item, idx) => (
                        <div key={idx} className="border border-slate-100 p-3 rounded-[2px] space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div><input type="text" value={item.title || item.name || ""} onChange={e => updateItemField(section.id, idx, item.name !== undefined ? "name" : "title", e.target.value)} placeholder="Title" className="w-full border border-slate-200 px-2 py-1.5 text-xs rounded-[2px]" /></div>
                            <div><input type="text" value={item.icon || ""} onChange={e => updateItemField(section.id, idx, "icon", e.target.value)} placeholder="Icon name" className="w-full border border-slate-200 px-2 py-1.5 text-xs rounded-[2px]" /></div>
                          </div>
                          {item.description !== undefined && <textarea value={item.description || ""} onChange={e => updateItemField(section.id, idx, "description", e.target.value)} rows={2} placeholder="Description" className="w-full border border-slate-200 px-2 py-1.5 text-xs rounded-[2px]" />}
                          {item.image !== undefined && <input type="text" value={item.image || ""} onChange={e => updateItemField(section.id, idx, "image", e.target.value)} placeholder="Image URL" className="w-full border border-slate-200 px-2 py-1.5 text-xs rounded-[2px]" />}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button onClick={() => saveSection(section)} className="flex items-center gap-2 bg-[#0A1628] text-white text-xs px-4 py-2 uppercase tracking-wider hover:bg-[#1a2c4e] rounded-[2px]">
                  <Save size={12} /> Save Section
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
